import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Product, ProductCategory, PetType } from '../../types/types';
import dataService from '../../data/dataService';
import '../../styles/Admin.css';

const CATEGORIES: ProductCategory[] = ['Food', 'Treats', 'Health', 'Accessories', 'Seasonal', 'Lifestyle'];
const PET_TYPES: PetType[] = ['Dog', 'Cat', 'Bird', 'Small Animal', 'All'];

interface ProductFormData {
  name: string;
  description: string;
  category: ProductCategory;
  price: string;
  imageUrl: string;
  petType: PetType;
  inStock: boolean;
  featured: boolean;
  tags: string;
}

const emptyForm: ProductFormData = {
  name: '', description: '', category: 'Food', price: '', imageUrl: '',
  petType: 'All', inStock: true, featured: false, tags: '',
};

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ProductFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const loadProducts = () => {
    setLoading(true);
    dataService.getProducts().then(setProducts).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadProducts(); }, []);

  const handleStockToggle = async (productId: number, currentStock: boolean) => {
    await dataService.toggleProductStock(productId, !currentStock);
    loadProducts();
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (p: Product) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      description: p.description,
      category: p.category,
      price: p.price.toString(),
      imageUrl: p.imageUrl || '',
      petType: p.petType,
      inStock: p.inStock,
      featured: p.featured,
      tags: (p.tags || []).join(', '),
    });
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const price = parseFloat(form.price);
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.description.trim()) { setError('Description is required'); return; }
    if (isNaN(price) || price < 0) { setError('Valid price is required'); return; }

    const payload: Partial<Product> = {
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      price,
      imageUrl: form.imageUrl.trim(),
      petType: form.petType,
      inStock: form.inStock,
      featured: form.featured,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
    };

    setSaving(true);
    try {
      if (editingId) {
        await dataService.updateProduct(editingId, payload);
      } else {
        await dataService.createProduct(payload);
      }
      closeForm();
      loadProducts();
    } catch (err: any) {
      setError(err.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await dataService.deleteProduct(id);
      setDeleteConfirm(null);
      loadProducts();
    } catch (err: any) {
      alert(err.message || 'Failed to delete product');
    }
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase()) ||
    p.petType.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Product Management</h1>
        <nav className="admin-nav">
          <Link to="/admin">Dashboard</Link>
          <Link to="/admin/orders">Orders</Link>
          <Link to="/admin/reviews">Reviews</Link>
          <Link to="/admin/discounts">Discounts</Link>
          <Link to="/admin/agent-dashboard">Agent Demo</Link>
        </nav>
      </div>

      <div className="admin-toolbar">
        <input
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="admin-search"
        />
        <button className="btn-primary" onClick={openCreate}>+ Add Product</button>
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Product' : 'Add New Product'}</h2>
              <button className="modal-close" onClick={closeForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="product-form">
              {error && <div className="form-error">{error}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input type="text" value={form.name} maxLength={200}
                    onChange={e => setForm({...form, name: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Price *</label>
                  <input type="number" step="0.01" min="0" value={form.price}
                    onChange={e => setForm({...form, price: e.target.value})} />
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea rows={3} value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})} />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select value={form.category}
                    onChange={e => setForm({...form, category: e.target.value as ProductCategory})}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Pet Type</label>
                  <select value={form.petType}
                    onChange={e => setForm({...form, petType: e.target.value as PetType})}>
                    {PET_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Image URL</label>
                <input type="text" value={form.imageUrl} placeholder="/images/products/my-product.svg"
                  onChange={e => setForm({...form, imageUrl: e.target.value})} />
              </div>

              <div className="form-group">
                <label>Tags (comma-separated)</label>
                <input type="text" value={form.tags} placeholder="organic, premium, sale"
                  onChange={e => setForm({...form, tags: e.target.value})} />
              </div>

              <div className="form-row">
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.inStock}
                    onChange={e => setForm({...form, inStock: e.target.checked})} />
                  In Stock
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.featured}
                    onChange={e => setForm({...form, featured: e.target.checked})} />
                  Featured
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm !== null && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <h3>Delete Product</h3>
            <p>Are you sure you want to delete this product? This cannot be undone.</p>
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-danger-fill" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p>Loading...</p> : filtered.length === 0 ? <p>No products found.</p> : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th><th>Name</th><th>Category</th><th>Pet Type</th>
              <th>Price</th><th>Rating</th><th>Stock</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>
                  <Link to={`/product/${p.id}`}>{p.name}</Link>
                  {p.featured && <span className="badge-featured">Featured</span>}
                </td>
                <td>{p.category}</td>
                <td>{p.petType}</td>
                <td>${p.price.toFixed(2)}</td>
                <td>{p.rating.toFixed(1)}</td>
                <td>
                  <button
                    className={`stock-toggle ${p.inStock ? 'in-stock' : 'out-of-stock'}`}
                    onClick={() => handleStockToggle(p.id, p.inStock)}
                  >
                    {p.inStock ? 'In Stock' : 'Out of Stock'}
                  </button>
                </td>
                <td className="actions-cell">
                  <button className="btn-edit" onClick={() => openEdit(p)} title="Edit">&#9998;</button>
                  <button className="btn-danger" onClick={() => setDeleteConfirm(p.id)} title="Delete">&#128465;</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="admin-summary">{filtered.length} product{filtered.length !== 1 ? 's' : ''}</div>
    </div>
  );
};

export default ProductManagement;
